import { useContext, useEffect, useState } from 'react';
import { IJackpotRoom } from '@api/types/jackpot';
import { useLazyQuery } from '@apollo/client';
import * as Queries from '@graphql/Queries';
import * as Utils from '@utils/utils';
import { Socket } from 'socket.io-client';
import { SocketContext } from '@context/socket';
import { ICoinflipGame } from '@api/types/Coinflip';

interface IActivePlayersState {
    blackjackActivePlayers: number;
    coinflipActivePlayers: number;
    jackpotActivePlayers: number;
}

interface IParams {
    fetch: {
        blackjack?: boolean;
        coinflip?: boolean;
        jackpot?: boolean;
    };
}

const useGamesActivePlayers = ({ fetch }: IParams): IActivePlayersState => {
    const socket: Socket = useContext(SocketContext);

    const [activePlayers, setActivePlayers] = useState<IActivePlayersState>({
        blackjackActivePlayers: 0,
        coinflipActivePlayers: 0,
        jackpotActivePlayers: 0,
    });

    const [fetchJackpotLobby] = useLazyQuery(Queries.GET_JACKPOT_LOBBY, {
        onCompleted: ({ lobby: { rooms } }) =>
            setActivePlayers((prevActivePlayers) => ({
                ...prevActivePlayers,

                /* Gets the players' amount that are currently joined to any active game in both rooms */
                jackpotActivePlayers: rooms.reduce(
                    (playersCount: number, room: IJackpotRoom) =>
                        room.current_game.players.length + playersCount,
                    0,
                ),
            })),
        onError: (err) => Utils.toastErrorMessage(err.message),
    });

    const [fetchBlackjackLobby] = useLazyQuery(Queries.GET_BLACKJACK_LOBBY, {
        onCompleted: ({ lobby: { rooms } }) =>
            setActivePlayers((prevActivePlayers) => ({
                ...prevActivePlayers,

                /* Gets the players' amount that are joined to any active game of the lobby */
                blackjackActivePlayers:
                    Utils.getActivePlayersInBlackjack(rooms),
            })),
        onError: (err) => Utils.toastErrorMessage(err.message),
    });

    const [fetchCoinflipGames] = useLazyQuery(Queries.GET_COINFLIP_GAMES, {
        onCompleted: ({ getAllCoinflipGames: games }) =>
            setActivePlayers((prevActivePlayers) => ({
                ...prevActivePlayers,

                /* Gets the players' amount that are joined to any active game */
                coinflipActivePlayers: Utils.getActivePlayersInCoinflip(games),
            })),
        onError: (err) => Utils.toastErrorMessage(err.message),
    });

    useEffect(() => {
        (async () => {
            fetch.blackjack && (await fetchBlackjackLobby());
            fetch.coinflip && (await fetchCoinflipGames());
            fetch.coinflip && (await fetchJackpotLobby());
        })();
    }, []);

    /* Coinflip updates */

    useEffect(() => {
        if (!fetch.coinflip) return;

        socket.on('coinflip-games', (gms: ICoinflipGame[]) =>
            setActivePlayers((prevPlayers) => ({
                ...prevPlayers,
                coinflipActivePlayers: Utils.getActivePlayersInCoinflip(gms),
            })),
        );

        socket.on('Cancel-CoinflipGame', () => {
            setActivePlayers((prevPlayers) => ({
                ...prevPlayers,
                coinflipActivePlayers: prevPlayers.coinflipActivePlayers - 1,
            }));
        });
    }, [socket]);

    const {
        blackjackActivePlayers,
        coinflipActivePlayers,
        jackpotActivePlayers,
    } = activePlayers;

    return {
        blackjackActivePlayers,
        coinflipActivePlayers,
        jackpotActivePlayers,
    };
};

export default useGamesActivePlayers;
